import React, { useState, useEffect } from 'react';
import {
  FaTrophy, FaPlus, FaHeart, FaMapMarkerAlt,
  FaCalendar, FaSpinner, FaTrash, FaClock,
  FaCheckCircle, FaTimesCircle, FaTimes, FaImage,
  FaEdit, FaEye, FaStar
} from 'react-icons/fa';
import {
  getMySuccessStories,
  submitSuccessStory,
  deleteMyStory,
  updateMyStory,
} from '../../Api/CoreApi';
import { message, Rate } from 'antd';

const SuccessStories = () => {
  const userRole = localStorage.getItem('role') || 'user';
  const isAdmin = ['admin', 'superadmin'].includes(userRole);
  const isStaff = userRole === 'staff';

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [filter, setFilter] = useState('all');
  const [viewStory, setViewStory] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const emptyForm = {
    name: '',
    husband_name: '',
    wife_name: '',
    marriage_date: '',
    text: '',
    city: '',
    district: '',
    state: '',
    img: null,
  };

  const [form, setForm] = useState(emptyForm);

  const stats = {
    total: stories.length,
    pending: stories.filter((s) => s.status === 'pending').length,
    approved: stories.filter((s) => s.status === 'approved').length,
    rejected: stories.filter((s) => s.status === 'rejected').length,
  };

  const displayStories =
    filter === 'all' ? stories : stories.filter((s) => s.status === filter);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const data = await getMySuccessStories();
      const list = Array.isArray(data) ? data : data?.data || [];
      setStories(list);
    } catch (err) {
      console.error(err);
      message.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'img' && files?.[0]) {
      const file = files[0];

      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
      if (!validTypes.includes(file.type)) {
        message.error("Only JPG, PNG, WEBP allowed");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        message.error("Max 5MB allowed");
        return;
      }

      setForm(prev => ({ ...prev, img: file }));

      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const removeImage = () => {
    setForm(prev => ({ ...prev, img: null }));
    setImagePreview(null);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setImagePreview(null);
    setEditingId(null);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (story) => {
    setEditingId(story.id);
    setForm({
      name: story.name || '',
      husband_name: story.husband_name || '',
      wife_name: story.wife_name || '',
      marriage_date: story.marriage_date
        ? String(story.marriage_date).slice(0, 10)
        : '',
      text: story.text || '',
      city: story.city || '',
      district: story.district || '',
      state: story.state || '',
      img: null,
    });
    const imgUrl = story.img_url || story.img || null;
    setImagePreview(imgUrl);
    setViewStory(null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.husband_name.trim()) {
      message.error("Husband's name required");
      return;
    }
    if (!form.wife_name.trim()) {
      message.error("Wife's name required");
      return;
    }
    if (!form.text.trim()) {
      message.error('Story required');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();

      const title =
        form.name?.trim() ||
        `${form.husband_name.trim()} & ${form.wife_name.trim()}`;
      formData.append('name', title);
      formData.append('husband_name', form.husband_name.trim());
      formData.append('wife_name', form.wife_name.trim());
      formData.append('text', form.text.trim());
      formData.append('city', form.city || '');
      formData.append('district', form.district || '');
      formData.append('state', form.state || '');

      if (form.marriage_date) {
        formData.append('marriage_date', form.marriage_date);
      }
      if (form.img) {
        formData.append('img', form.img);
      }

      let result;
      if (editingId) {
        result = await updateMyStory(editingId, formData);
      } else {
        result = await submitSuccessStory(formData);
      }

      const ok = result && (result.success || result.id || result.data?.id || result.name);

      if (ok) {
        message.success(
          editingId
            ? '✅ Story updated!' + (!isAdmin ? ' (Pending admin approval)' : '')
            : isAdmin
              ? '🎉 Story published!'
              : '✅ Story submitted! Admin approval ke baad dikhegi.'
        );
        resetForm();
        setShowForm(false);
        fetchStories();
      } else {
        message.error(result?.errors ? JSON.stringify(result.errors) : 'Failed to save');
      }
    } catch (err) {
      console.error(err);
      const errs = err?.response?.data;
      if (errs && typeof errs === 'object') {
        const msg = errs.errors
          ? JSON.stringify(errs.errors)
          : Object.entries(errs)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('\n');
        message.error(msg || 'Failed to submit story');
      } else {
        message.error('Failed to submit story');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this story?')) return;
    try {
      await deleteMyStory(id);
      message.success('Story deleted');
      setViewStory(null);
      fetchStories();
    } catch {
      message.error('Failed to delete');
    }
  };

  const getImage = (story) => story?.img_url || story?.img || null;

  const StatusBadge = ({ status }) => {
    const config = {
      pending: { bg: '#fff3e0', color: '#f57c00', border: '#ffcc80', icon: <FaClock size={9} />, label: 'Pending' },
      approved: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', icon: <FaCheckCircle size={9} />, label: 'Approved' },
      rejected: { bg: '#ffebee', color: '#c62828', border: '#ffcdd2', icon: <FaTimesCircle size={9} />, label: 'Rejected' },
    };
    const s = config[status] || config.pending;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '3px 8px', borderRadius: 999,
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap'
      }}>
        {s.icon} {s.label}
      </span>
    );
  };

  const Stars = ({ value = 5, size = 11 }) => (
    <span style={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <FaStar key={i} size={size} color={i <= Number(value) ? '#f59e0b' : '#e5e7eb'} />
      ))}
    </span>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

        .ss-page { background: #fff; min-height: 100vh; padding: 20px 16px; }
        .ss-wrap { max-width: 1200px; margin: 0 auto; }

        .ss-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
        .ss-title { font-size: 22px; font-weight: 800; color: #1a0a10; display: flex; align-items: center; gap: 10px; margin: 0; }
        .ss-role-tag { font-size: 11px; padding: 3px 10px; border-radius: 999px; font-weight: 700; }
        .ss-add-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border: none; border-radius: 12px; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 4px 14px rgba(255,0,85,.25); transition: all .3s; }
        .ss-add-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(255,0,85,.3); }

        .ss-info { border-radius: 12px; padding: 11px 16px; margin-bottom: 18px; font-size: 12.5px; font-weight: 600; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

        .ss-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 18px; }
        .ss-stat { padding: 14px; border-radius: 14px; border: 2px solid; text-align: center; cursor: pointer; transition: all .25s; }
        .ss-stat:hover { transform: translateY(-2px); }
        .ss-stat-num { font-size: 24px; font-weight: 900; }
        .ss-stat-label { font-size: 11px; font-weight: 700; margin-top: 3px; }

        .ss-filters { display: flex; gap: 8px; margin-bottom: 18px; flex-wrap: wrap; }
        .ss-filter { padding: 7px 16px; border-radius: 999px; border: 1.5px solid #ffe4ec; background: #fff; font-size: 12px; font-weight: 700; cursor: pointer; color: #888; transition: all .25s; }
        .ss-filter.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: transparent; box-shadow: 0 4px 12px rgba(255,0,85,.2); }

        /* ============= COMPACT LIST (UPDATED - Date & Location Separate Columns) ============= */
        .ss-list { display: flex; flex-direction: column; gap: 8px; }

        .ss-item {
          display: grid;
          grid-template-columns: 60px 1.5fr 110px 150px 100px 120px;
          gap: 12px;
          padding: 10px 14px;
          background: #fff;
          border: 1px solid #ffe4ec;
          border-radius: 12px;
          align-items: center;
          transition: all .2s;
          animation: fadeUp .3s ease;
        }
        .ss-item:hover { border-color: #ffb3c6; box-shadow: 0 4px 12px rgba(255,0,85,0.08); transform: translateY(-1px); }

        .ss-item-img { width: 56px; height: 56px; border-radius: 10px; object-fit: cover; object-position: top; border: 1.5px solid #ffe4ec; }
        .ss-item-img-ph { width: 56px; height: 56px; border-radius: 10px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); display: flex; align-items: center; justify-content: center; font-size: 22px; border: 1.5px solid #ffd3df; }

        .ss-item-info { min-width: 0; }
        .ss-item-couple { font-size: 13px; font-weight: 700; color: #1a0a10; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ss-item-title { font-size: 11px; color: #ff4d88; font-style: italic; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ss-item-preview { font-size: 11px; color: #888; margin-top: 3px; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }

        /* ✅ NEW: Separate Date Column */
        .ss-item-date-col {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 10px;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          border: 1px solid #ffd3df;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          color: #c2185b;
          min-width: 0;
        }
        .ss-item-date-col svg { color: #ff0055; flex-shrink: 0; }
        .ss-item-date-col span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        /* ✅ NEW: Separate Location Column */
        .ss-item-location-col {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 10px;
          background: linear-gradient(135deg, #f3e5f5, #e1bee7);
          border: 1px solid #ce93d8;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          color: #6a1b9a;
          min-width: 0;
        }
        .ss-item-location-col svg { color: #8e24aa; flex-shrink: 0; }
        .ss-item-location-col span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }

        .ss-item-empty-col {
          padding: 8px 10px;
          background: #f9f9f9;
          border: 1px dashed #ddd;
          border-radius: 8px;
          font-size: 10px;
          color: #bbb;
          text-align: center;
          font-style: italic;
        }

        .ss-item-status-col { display: flex; justify-content: center; }

        .ss-item-actions { display: flex; gap: 5px; justify-content: flex-end; }
        .ss-item-btn {
          width: 30px; height: 30px; border-radius: 8px; border: none;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 11px; cursor: pointer; transition: all .2s;
        }
        .ss-item-btn.view { background: #e3f2fd; color: #1565c0; }
        .ss-item-btn.view:hover { background: #1565c0; color: #fff; }
        .ss-item-btn.edit { background: #fff7e6; color: #d48806; }
        .ss-item-btn.edit:hover { background: #faad14; color: #fff; }
        .ss-item-btn.delete { background: #ffebee; color: #f44336; }
        .ss-item-btn.delete:hover { background: #f44336; color: #fff; }

        .ss-reject-note { margin-top: 4px; padding: 4px 8px; background: #ffebee; border-radius: 6px; font-size: 10px; color: #c62828; border: 1px solid #ffcdd2; }

        .ss-empty { text-align: center; padding: 60px 20px; border: 2px dashed #ffd3df; border-radius: 16px; margin-top: 10px; }

        .ss-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 16px; }
        .ss-modal { background: #fff; border-radius: 24px; width: 100%; max-width: 620px; max-height: 90vh; overflow-y: auto; box-shadow: 0 24px 64px rgba(0,0,0,0.2); animation: fadeUp .3s ease; }
        .ss-modal-head { padding: 20px 24px 16px; border-bottom: 2px solid #fff0f3; display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; background: #fff; z-index: 1; border-radius: 24px 24px 0 0; }
        .ss-modal-title { font-size: 18px; font-weight: 800; color: #1a0a10; display: flex; align-items: center; gap: 10px; margin: 0; }
        .ss-modal-close { background: none; border: none; font-size: 18px; color: #bbb; cursor: pointer; padding: 4px; line-height: 1; }
        .ss-modal-close:hover { color: #ff0055; }
        .ss-modal-body { padding: 22px; }

        .ss-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .ss-fg { display: flex; flex-direction: column; gap: 5px; }
        .ss-fg.full { grid-column: 1/-1; }
        .ss-label { font-size: 11px; font-weight: 700; color: #c2185b; text-transform: uppercase; letter-spacing: .5px; }
        .ss-required { color: #ff0055; }
        .ss-input { padding: 10px 13px; border: 1.5px solid #ffe4ec; border-radius: 10px; font-size: 13px; outline: none; color: #1a0a10; width: 100%; transition: border .25s; background: #fff; }
        .ss-input:focus { border-color: #ff0055; box-shadow: 0 0 0 3px rgba(255,0,85,.07); }
        .ss-textarea { resize: vertical; min-height: 90px; }

        .ss-submit-btn { width: 100%; padding: 13px; border: none; border-radius: 12px; font-size: 14px; font-weight: 700; cursor: pointer; margin-top: 14px; transition: all .3s; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .ss-submit-btn:disabled { opacity: .6; cursor: not-allowed; }

        .ss-img-upload-box {
          border: 2px dashed #ff80aa;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          border-radius: 12px;
          padding: 30px 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 160px;
        }
        .ss-img-upload-box:hover {
          border-color: #ff0055;
          background: linear-gradient(135deg, #ffe4ec, #ffd6e7);
          transform: translateY(-2px);
        }
        .ss-img-upload-box input[type="file"] { display: none; }
        .ss-img-preview-wrap {
          position: relative;
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
          border: 2px solid #ff0055;
          background: #fff;
        }
        .ss-img-preview-wrap img {
          width: 100%;
          max-height: 240px;
          object-fit: cover;
          display: block;
        }
        .ss-img-remove-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 34px;
          height: 34px;
          background: rgba(244, 67, 54, 0.95);
          color: #fff;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          transition: all 0.2s;
        }
        .ss-img-remove-btn:hover { background: #d32f2f; transform: scale(1.1); }
        .ss-img-change-btn {
          margin-top: 8px;
          padding: 8px 16px;
          background: #fff0f3;
          color: #ff0055;
          border: 1.5px solid #ff0055;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }
        .ss-img-change-btn:hover { background: #ff0055; color: #fff; }

        .ss-view-img { width: 100%; max-height: 280px; object-fit: contain; background: #111; border-radius: 14px; margin-bottom: 16px; }
        .ss-view-img-ph { width: 100%; height: 160px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 52px; margin-bottom: 16px; }
        .ss-view-names { font-size: 20px; font-weight: 800; color: #1a0a10; margin-bottom: 8px; word-break: break-word; }
        .ss-view-meta { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
        .ss-view-meta-item { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #888; }
        .ss-view-meta-item svg { color: #ff4d88; }
        .ss-view-text { font-size: 13.5px; color: #444; line-height: 1.8; padding: 16px; background: linear-gradient(135deg, #fffafc, #fff0f3); border-radius: 12px; border: 1.5px solid #ffe4ec; white-space: pre-wrap; word-break: break-word; }

        /* ============= RESPONSIVE ============= */
        @media (max-width: 1024px) {
          .ss-item {
            grid-template-columns: 56px 1.4fr 100px 130px 100px 110px;
            gap: 10px;
          }
        }

        @media (max-width: 900px) {
          .ss-item {
            grid-template-columns: 56px 1fr 90px;
            gap: 10px;
            padding: 10px;
          }
          
          .ss-item-date-col,
          .ss-item-location-col,
          .ss-item-status-col {
            display: none;
          }
          
          .ss-item-actions { grid-column: 1 / -1; justify-content: center; margin-top: 6px; border-top: 1px dashed #ffe4ec; padding-top: 8px; }
          .ss-item-mobile-extra { display: flex !important; gap: 6px; margin-top: 6px; flex-wrap: wrap; align-items: center; }
          .ss-item-mobile-extra .mobile-chip {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 8px;
            border-radius: 6px;
            font-size: 10px;
            font-weight: 600;
          }
          .ss-item-mobile-extra .mobile-chip.date {
            background: #fff0f3;
            color: #c2185b;
            border: 1px solid #ffd3df;
          }
          .ss-item-mobile-extra .mobile-chip.location {
            background: #f3e5f5;
            color: #6a1b9a;
            border: 1px solid #ce93d8;
          }
        }
        .ss-item-mobile-extra { display: none; }

        @media (max-width: 768px) {
          .ss-stats { grid-template-columns: repeat(2, 1fr); }
          .ss-page { padding: 12px 10px; }
          .ss-title { font-size: 18px; }
          .ss-add-btn { padding: 8px 14px; font-size: 12px; }
        }
        @media (max-width: 500px) {
          .ss-form-grid { grid-template-columns: 1fr; }
          .ss-fg.full { grid-column: 1; }
          .ss-item { grid-template-columns: 50px 1fr 80px; padding: 8px; gap: 8px; }
          .ss-item-img, .ss-item-img-ph { width: 48px; height: 48px; }
        }
      `}</style>

      <div className="ss-page">
        <div className="ss-wrap">
          <div className="ss-header">
            <h1 className="ss-title">
              <FaTrophy style={{ color: '#ff0055' }} />
              Success Stories
              <span
                className="ss-role-tag"
                style={{
                  background: isStaff ? '#e3f2fd' : '#fff0f3',
                  color: isStaff ? '#1565c0' : '#ff0055',
                }}
              >
                {isStaff ? '👔 Staff' : isAdmin ? '🛡 Admin' : '👤 User'}
              </span>
            </h1>
            <button className="ss-add-btn" onClick={openAdd}>
              <FaPlus size={12} /> Add Story
            </button>
          </div>

          <div className="ss-stats">
            {[
              { key: 'all', label: 'Total', num: stats.total, bg: '#fff0f3', border: '#ffd3df', color: '#ff0055' },
              { key: 'pending', label: 'Pending', num: stats.pending, bg: '#fff3e0', border: '#ffcc80', color: '#f57c00' },
              { key: 'approved', label: 'Approved', num: stats.approved, bg: '#e8f5e9', border: '#a5d6a7', color: '#2e7d32' },
              { key: 'rejected', label: 'Rejected', num: stats.rejected, bg: '#ffebee', border: '#ffcdd2', color: '#c62828' },
            ].map((s) => (
              <div
                key={s.key}
                className="ss-stat"
                style={{ background: s.bg, borderColor: filter === s.key ? s.color : s.border }}
                onClick={() => setFilter(s.key)}
              >
                <div className="ss-stat-num" style={{ color: s.color }}>{s.num}</div>
                <div className="ss-stat-label" style={{ color: s.color }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div className="ss-filters">
            {[
              { key: 'all', label: `All (${stats.total})` },
              { key: 'pending', label: `⏳ Pending (${stats.pending})` },
              { key: 'approved', label: `✅ Approved (${stats.approved})` },
              { key: 'rejected', label: `❌ Rejected (${stats.rejected})` },
            ].map((f) => (
              <button
                key={f.key}
                className={`ss-filter ${filter === f.key ? 'active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <FaSpinner size={36} style={{ color: '#ff0055', animation: 'spin 1s linear infinite' }} />
              <p style={{ color: '#888', marginTop: 12 }}>Loading stories...</p>
            </div>
          ) : displayStories.length > 0 ? (
            <div className="ss-list">
              {displayStories.map((story) => {
                const locationStr = [story.city, story.district, story.state].filter(Boolean).join(', ');
                const dateStr = story.marriage_date
                  ? new Date(story.marriage_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                  : null;

                return (
                  <div key={story.id} className="ss-item">
                    {/* Image */}
                    {getImage(story) ? (
                      <img src={getImage(story)} alt="" className="ss-item-img" />
                    ) : (
                      <div className="ss-item-img-ph">💑</div>
                    )}

                    {/* Info */}
                    <div className="ss-item-info">
                      <p className="ss-item-couple">
                        {story.husband_name && story.wife_name
                          ? `${story.husband_name} ❤️ ${story.wife_name}`
                          : story.name || 'Couple'}
                      </p>
                      {story.name && story.name !== `${story.husband_name} & ${story.wife_name}` && (
                        <p className="ss-item-title">"{story.name}"</p>
                      )}
                      <p className="ss-item-preview">{story.text}</p>
                      {story.status === 'rejected' && story.admin_note && (
                        <div className="ss-reject-note">❌ {story.admin_note}</div>
                      )}

                      {/* Mobile-only chips (visible below 900px) */}
                      <div className="ss-item-mobile-extra">
                        {dateStr && (
                          <span className="mobile-chip date">
                            <FaCalendar size={9} /> {dateStr}
                          </span>
                        )}
                        {locationStr && (
                          <span className="mobile-chip location">
                            <FaMapMarkerAlt size={9} /> {locationStr}
                          </span>
                        )}
                        <StatusBadge status={story.status} />
                      </div>
                    </div>

                    {/* ✅ Separate Date Column */}
                    {dateStr ? (
                      <div className="ss-item-date-col" title={dateStr}>
                        <FaCalendar size={10} />
                        <span>{dateStr}</span>
                      </div>
                    ) : (
                      <div className="ss-item-empty-col">No date</div>
                    )}

                    {/* ✅ Separate Location Column */}
                    {locationStr ? (
                      <div className="ss-item-location-col" title={locationStr}>
                        <FaMapMarkerAlt size={10} />
                        <span>{locationStr}</span>
                      </div>
                    ) : (
                      <div className="ss-item-empty-col">No location</div>
                    )}

                    {/* Status Column */}
                    <div className="ss-item-status-col">
                      <StatusBadge status={story.status} />
                    </div>

                    {/* Actions */}
                    <div className="ss-item-actions">
                      <button className="ss-item-btn view" onClick={() => setViewStory(story)} title="View"><FaEye /></button>
                      <button className="ss-item-btn edit" onClick={() => openEdit(story)} title="Edit"><FaEdit /></button>
                      <button className="ss-item-btn delete" onClick={() => handleDelete(story.id)} title="Delete"><FaTrash /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="ss-empty">
              <p style={{ fontSize: 52, marginBottom: 14 }}>💑</p>
              <h3 style={{ color: '#c2185b', fontWeight: 800, marginBottom: 8, fontSize: 18 }}>
                {filter !== 'all' ? `No ${filter} stories` : 'No Stories Yet'}
              </h3>
              <p style={{ color: '#aaa', fontSize: 13, marginBottom: 20 }}>
                Share a success story to inspire others!
              </p>
              {filter === 'all' && (
                <button className="ss-add-btn" style={{ margin: '0 auto', display: 'inline-flex' }} onClick={openAdd}>
                  <FaPlus size={12} /> Add First Story
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== ADD / EDIT MODAL ===== */}
      {showForm && (
        <div className="ss-overlay" onClick={() => { setShowForm(false); resetForm(); }}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ss-modal-head">
              <h3 className="ss-modal-title">
                <FaTrophy style={{ color: '#ff0055' }} />
                {editingId ? 'Edit Success Story' : 'Share Success Story'}
              </h3>
              <button className="ss-modal-close" onClick={() => { setShowForm(false); resetForm(); }}>
                <FaTimes />
              </button>
            </div>

            <div className="ss-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="ss-form-grid">

                  <div className="ss-fg full">
                    <label className="ss-label">
                      Couple Photo {!editingId && <span style={{ color: '#999', fontWeight: 500 }}>(optional)</span>}
                    </label>

                    {imagePreview ? (
                      <div className="ss-img-preview-wrap">
                        <img src={imagePreview} alt="Preview" />
                        <button
                          type="button"
                          className="ss-img-remove-btn"
                          onClick={removeImage}
                          title="Remove photo"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : (
                      <label className="ss-img-upload-box">
                        <input
                          type="file"
                          name="img"
                          accept="image/jpeg,image/png,image/jpg,image/webp"
                          onChange={handleChange}
                        />
                        <FaImage size={32} style={{ color: '#ff0055', marginBottom: 10 }} />
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#ff0055' }}>
                          Click to Upload Photo
                        </div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                          JPG, PNG, WEBP • Max 5MB
                        </div>
                      </label>
                    )}

                    {imagePreview && (
                      <label style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                        <input
                          type="file"
                          name="img"
                          accept="image/jpeg,image/png,image/jpg,image/webp"
                          onChange={handleChange}
                          style={{ display: 'none' }}
                        />
                        <span className="ss-img-change-btn">
                          <FaImage size={11} /> Change Photo
                        </span>
                      </label>
                    )}
                  </div>

                  <div className="ss-fg full">
                    <label className="ss-label">Story Title (optional)</label>
                    <input className="ss-input" type="text" name="name" value={form.name} onChange={handleChange} placeholder="e.g. A Beautiful Love Story" />
                  </div>

                  <div className="ss-fg">
                    <label className="ss-label">Husband's Name <span className="ss-required">*</span></label>
                    <input className="ss-input" type="text" name="husband_name" value={form.husband_name} onChange={handleChange} placeholder="Husband's full name" required />
                  </div>

                  <div className="ss-fg">
                    <label className="ss-label">Wife's Name <span className="ss-required">*</span></label>
                    <input className="ss-input" type="text" name="wife_name" value={form.wife_name} onChange={handleChange} placeholder="Wife's full name" required />
                  </div>

                  <div className="ss-fg">
                    <label className="ss-label">Marriage Date</label>
                    <input className="ss-input" type="date" name="marriage_date" value={form.marriage_date} onChange={handleChange} />
                  </div>

                  <div className="ss-fg">
                    <label className="ss-label">City</label>
                    <input className="ss-input" type="text" name="city" value={form.city} onChange={handleChange} placeholder="City" />
                  </div>

                  <div className="ss-fg">
                    <label className="ss-label">District</label>
                    <input className="ss-input" type="text" name="district" value={form.district} onChange={handleChange} placeholder="District" />
                  </div>

                  <div className="ss-fg">
                    <label className="ss-label">State</label>
                    <input className="ss-input" type="text" name="state" value={form.state} onChange={handleChange} placeholder="State" />
                  </div>

                  <div className="ss-fg full">
                    <label className="ss-label">Their Story <span className="ss-required">*</span></label>
                    <textarea className="ss-input ss-textarea" name="text" value={form.text} onChange={handleChange} placeholder="How they met, their journey together..." required />
                  </div>
                </div>

                <button
                  type="submit"
                  className="ss-submit-btn"
                  disabled={submitting}
                  style={{ background: 'linear-gradient(135deg, #ff0055, #ff4d88)', color: '#fff', boxShadow: '0 6px 18px rgba(255,0,85,.25)' }}
                >
                  {submitting ? (
                    <><FaSpinner size={13} style={{ animation: 'spin 1s linear infinite' }} />{editingId ? 'Updating...' : 'Submitting...'}</>
                  ) : (
                    <><FaHeart size={13} />{editingId ? 'Update Story' : 'Submit Story'}</>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ===== VIEW MODAL ===== */}
      {viewStory && (
        <div className="ss-overlay" onClick={() => setViewStory(null)}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ss-modal-head">
              <h3 className="ss-modal-title">
                <FaHeart style={{ color: '#ff0055' }} /> Story Details
              </h3>
              <button className="ss-modal-close" onClick={() => setViewStory(null)}><FaTimes /></button>
            </div>

            <div className="ss-modal-body">
              {getImage(viewStory) ? (
                <img src={getImage(viewStory)} alt="couple" className="ss-view-img" />
              ) : (
                <div className="ss-view-img-ph">💑</div>
              )}

              <h2 className="ss-view-names">
                {viewStory.husband_name && viewStory.wife_name
                  ? `${viewStory.husband_name} ❤️ ${viewStory.wife_name}`
                  : viewStory.name}
              </h2>

              {viewStory.name && (
                <p style={{ color: '#ff4d88', fontStyle: 'italic', marginBottom: 10, fontSize: 14 }}>
                  "{viewStory.name}"
                </p>
              )}

              <div style={{ marginBottom: 12 }}>
                <StatusBadge status={viewStory.status} />
              </div>

              <div className="ss-view-meta">
                {viewStory.marriage_date && (
                  <span className="ss-view-meta-item">
                    <FaCalendar size={10} />
                    {new Date(viewStory.marriage_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </span>
                )}
                {(viewStory.city || viewStory.district || viewStory.state) && (
                  <span className="ss-view-meta-item">
                    <FaMapMarkerAlt size={10} />
                    {[viewStory.city, viewStory.district, viewStory.state].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>

              <div className="ss-view-text">{viewStory.text}</div>

              {viewStory.status === 'rejected' && viewStory.admin_note && (
                <div style={{ marginTop: 14, padding: '10px 14px', background: '#ffebee', borderRadius: 10, border: '1px solid #ffcdd2', fontSize: 13, color: '#c62828' }}>
                  ❌ <strong>Rejection Reason:</strong> {viewStory.admin_note}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button
                  className="ss-item-btn edit"
                  style={{ flex: 1, height: 40, borderRadius: 10, fontSize: 13, fontWeight: 700, gap: 6 }}
                  onClick={() => openEdit(viewStory)}
                >
                  <FaEdit size={12} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(viewStory.id)}
                  style={{ flex: 1, padding: '10px', background: '#ffebee', color: '#f44336', border: '1px solid #ffcdd2', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <FaTrash size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SuccessStories;